'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';

import { selectActiveTenant } from '@/lib/active-tenant';
import { authApi } from '@/lib/api/auth';
import { AgentApiError, assistantApi, type ResolutionContext } from '@/lib/api/assistant';
import { effectiveStatus } from '@/lib/assistant/actions';
import { pageContextFrom } from '@/lib/assistant/page-context';
import {
  appendUserText,
  emptyTurn,
  fromBlocks,
  reduceAgentEvent,
  type TurnState,
} from '@/lib/assistant/blocks';
import {
  assistantIsVisible,
  conversationStorageKey,
  turnNotice,
} from '@/lib/assistant/session';
import type { AgentEvent, AgentStatus } from '@/lib/api/types';

export type OverlayState = 'closed' | 'open' | 'minimized';

export interface AssistantError {
  code: string;
  message: string;
  /** True when sending the same thing again is a reasonable next step. */
  retryable: boolean;
}

export interface AssistantContextValue {
  status: AgentStatus | undefined;
  visible: boolean;
  overlay: OverlayState;
  open: () => void;
  minimize: () => void;
  close: () => void;
  toggle: () => void;
  showHistory: boolean;
  setShowHistory: (value: boolean) => void;
  conversationId: string | null;
  turn: TurnState;
  streaming: boolean;
  notice: string | null;
  error: AssistantError | null;
  hasPendingActions: boolean;
  send: (text: string) => Promise<void>;
  resolve: (kind: 'apply' | 'cancel' | 'applyAll', id: string) => Promise<void>;
  stop: () => void;
  newConversation: () => void;
  openConversation: (id: string) => Promise<void>;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

export function useAssistant(): AssistantContextValue {
  const value = useContext(AssistantContext);
  if (!value) throw new Error('useAssistant must be used within an AssistantProvider');
  return value;
}

export function AssistantProvider({
  children,
  resolveContext: resolveContextProp,
}: {
  children: ReactNode;
  resolveContext?: () => ResolutionContext;
}) {
  const queryClient = useQueryClient();

  // `usePathname()`, not `useSearchParams()` — this provider is mounted by
  // `app/(app)/layout.tsx`, so a `useSearchParams()` here would put a Suspense
  // boundary requirement on every dashboard route at build time. The query
  // string is read imperatively via `window.location.search` inside the
  // callback below instead: it only ever runs client-side at send time, so it
  // needs no hook, and it is strictly fresher than a value captured at render.
  const pathname = usePathname();

  const defaultResolveContext = useCallback((): ResolutionContext => {
    const search = typeof window === 'undefined' ? '' : window.location.search;
    return {
      page_context: pageContextFrom(pathname ?? '', new URLSearchParams(search)),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    };
  }, [pathname]);

  const resolveContext = resolveContextProp ?? defaultResolveContext;
  const [overlay, setOverlay] = useState<OverlayState>('closed');
  const [showHistory, setShowHistory] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turn, setTurn] = useState<TurnState>(emptyTurn);
  const [streaming, setStreaming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<AssistantError | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Shadows `conversationId` for code that has to know which conversation is
  // active *now* rather than which one was active when its closure was made —
  // see the reload guard in `consume`'s `finally`.
  const conversationIdRef = useRef<string | null>(null);

  /**
   * The user has moved on: stop the in-flight stream and drop the turn-level
   * state that belonged to it.
   *
   * `abortRef` is the single point where "moved on" has to be signalled, and
   * it has now been missed once per entry point — first in `newConversation()`,
   * then in `openConversation()`. Each time, the abandoned stream's remaining
   * `text_delta`s and its `confirmation_required` card went on appending into
   * whatever the user was looking at next, and a card resolved there posts the
   * *old* conversation's action id. So every entry point that walks away from
   * a running turn calls this instead of reaching for `abortRef` itself, and a
   * fifth one inherits the guard rather than having to re-derive it.
   *
   * `notice` and `error` go with the abort: both describe the abandoned turn,
   * so leaving them set renders one conversation's outcome under another's
   * transcript.
   */
  const abandonTurn = useCallback(() => {
    abortRef.current?.abort();
    setNotice(null);
    setError(null);
  }, []);

  // A leaked AbortController keeps a billed LLM stream running server-side
  // after this provider is gone, and any `setState` it still issues would hit
  // an unmounted component. Today tenant-switch and logout are full page
  // navigations that tear this down anyway — that's luck, not a guarantee for
  // every future mount path, so abort explicitly on unmount. The state clearing
  // inside `abandonTurn` is a no-op once unmounted (React 19); the abort is the
  // part that matters here, and going through the shared helper is what keeps
  // this path from drifting away from the other three.
  useEffect(() => {
    return () => abandonTurn();
  }, [abandonTurn]);

  const { data: tenantsData } = useQuery({
    queryKey: ['user-tenants'],
    queryFn: () => authApi.getTenants(),
  });
  const tenantId = selectActiveTenant(tenantsData)?.id ?? null;

  const { data: status } = useQuery({
    queryKey: ['agent-status', tenantId],
    queryFn: () => assistantApi.status(),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const rememberConversation = useCallback(
    (id: string) => {
      setConversationId(id);
      conversationIdRef.current = id;
      if (tenantId) {
        try {
          sessionStorage.setItem(conversationStorageKey(tenantId), id);
        } catch {
          // Private mode or a blocked store: the conversation simply does not
          // survive a reload. Never a reason to fail the turn.
        }
      }
    },
    [tenantId],
  );

  const forgetConversation = useCallback(() => {
    setConversationId(null);
    conversationIdRef.current = null;
    if (tenantId) {
      try {
        sessionStorage.removeItem(conversationStorageKey(tenantId));
      } catch {
        // See rememberConversation.
      }
    }
  }, [tenantId]);

  /**
   * Replace the transcript with a stored conversation.
   *
   * Deliberately does NOT abandon the running turn: the mid-stream
   * `conversation_busy` reload in `consume` reaches this for the conversation
   * the stream itself belongs to, where the turn's `error` is the explanation
   * the user still needs to read. Anything driven by the user goes through
   * `openConversation` below.
   */
  const loadConversation = useCallback(
    async (id: string) => {
      const detail = await assistantApi.getConversation(id);
      rememberConversation(id);
      setTurn(fromBlocks(detail.blocks));
      setShowHistory(false);
    },
    [rememberConversation],
  );

  const openConversation = useCallback(
    async (id: string) => {
      // Reopening an earlier conversation while a turn is streaming means
      // abandon that turn — the same hazard `newConversation()` carries, and
      // worse here: the events would land in a real, persisted conversation
      // the user believes they are reading, `resolve()` would post the old
      // conversation's action id against the new one, and `streaming` would
      // stay true behind the history view where there is no Stop button to
      // press. Do NOT instead disable History while streaming; the user's
      // intent is to leave the turn, not to be blocked from doing so.
      abandonTurn();
      await loadConversation(id);
    },
    [abandonTurn, loadConversation],
  );

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    const stored = tenantId
      ? (() => {
          try {
            return sessionStorage.getItem(conversationStorageKey(tenantId));
          } catch {
            return null;
          }
        })()
      : null;
    if (stored) {
      rememberConversation(stored);
      return stored;
    }
    const created = await assistantApi.createConversation();
    rememberConversation(created.id);
    return created.id;
  }, [conversationId, rememberConversation, tenantId]);

  /**
   * Drive one stream into the transcript. Shared by send, apply and cancel.
   *
   * `consume` owns the turn's AbortController: it creates it, publishes it on
   * `abortRef`, and is the only thing allowed to clear it. That ownership is
   * load-bearing. The call sites used to create the controller themselves while
   * this `finally` cleared `abortRef` unconditionally, so a turn unwinding late
   * — and both entry points `await` before they publish, so "late" is ordinary
   * — would null out a *newer* turn's controller. `abandonTurn()` then aborted
   * nothing: Stop did nothing, and every path that walks away from a running
   * turn silently stopped working. That is the exact failure the comment on
   * `abandonTurn` says has already been missed twice.
   *
   * Callers pass a `start(signal)` rather than a live generator, so a call site
   * cannot hold a signal this provider does not know about.
   */
  const consume = useCallback(
    async (start: (signal: AbortSignal) => AsyncGenerator<AgentEvent>, id: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      setNotice(null);
      setError(null);
      let reload = false;

      try {
        for await (const event of start(controller.signal)) {
          setTurn((current) => reduceAgentEvent(current, event));

          if (event.type === 'action_resolved' && event.status === 'applied') {
            // The change landed in Postgres and Redis; any open page may now be
            // stale, and the assistant does not know which feature it touched.
            void queryClient.invalidateQueries();
          }
          if (event.type === 'error') {
            // A mid-stream conversation_busy is NOT the 409 of the same name:
            // work already happened, so reopen rather than offer a retry.
            reload = event.code === 'conversation_busy';
            setError({ code: event.code, message: event.message, retryable: !reload });
          }
          if (event.type === 'turn_done') {
            setNotice(turnNotice(event.status));
            void queryClient.invalidateQueries({ queryKey: ['agent-status', tenantId] });
          }
        }
      } catch (caught) {
        if (caught instanceof AgentApiError) {
          setError({
            code: caught.code,
            message: caught.message,
            // A 409 conversation_busy means nothing happened at all.
            retryable: caught.code === 'conversation_busy' || caught.code === 'rate_limited',
          });
          if (caught.code === 'quota_exhausted') {
            void queryClient.invalidateQueries({ queryKey: ['agent-status', tenantId] });
          }
          if (caught.code === 'not_found') {
            // R-P9: the plan's API table requires this even though no other
            // task implements it. A stale per-tenant conversation id (the
            // workspace moved on, or the conversation was deleted elsewhere)
            // 404s forever otherwise — clear it so the next send starts a
            // fresh conversation instead of permanently failing.
            forgetConversation();
          }
        } else if ((caught as { name?: string }).name !== 'AbortError') {
          setError({ code: 'network', message: 'The connection dropped.', retryable: true });
        }
      } finally {
        // Only the turn that still owns `abortRef` may wind anything down. A
        // turn that unwinds after another has replaced it owns nothing: clearing
        // the controller would disarm its successor, and clearing `streaming`
        // would report a turn that is still in flight as finished.
        if (abortRef.current === controller) {
          setStreaming(false);
          abortRef.current = null;
        }
        // `id` is the conversation this stream belonged to, captured when the
        // turn started. If the user has since reopened a different one, this
        // reload would silently yank them back and rewrite the per-tenant
        // sessionStorage key to a conversation they have left — so check the
        // live id, not the `conversationId` state this closure captured.
        if (reload && conversationIdRef.current === id) {
          await loadConversation(id).catch(() => undefined);
        }
      }
    },
    [forgetConversation, loadConversation, queryClient, tenantId],
  );

  const send = useCallback(
    async (text: string) => {
      const id = await ensureConversation();
      // The stream never echoes the user's own message back.
      setTurn((current) => appendUserText(current, text));
      await consume(
        (signal) => assistantApi.sendMessage({ conversationId: id, text, ...resolveContext() }, { signal }),
        id,
      );
    },
    [consume, ensureConversation, resolveContext],
  );

  const resolve = useCallback(
    async (kind: 'apply' | 'cancel' | 'applyAll', id: string) => {
      const conversation = await ensureConversation();
      const context = resolveContext();
      await consume(
        (signal) => {
          const options = { signal };
          return kind === 'apply'
            ? assistantApi.applyAction(id, context, options)
            : kind === 'cancel'
              ? assistantApi.cancelAction(id, context, options)
              : assistantApi.applyAll(conversation, context, options);
        },
        conversation,
      );
    },
    [consume, ensureConversation, resolveContext],
  );

  const stop = useCallback(() => {
    abandonTurn();
  }, [abandonTurn]);

  const newConversation = useCallback(() => {
    // "New chat" while a turn is streaming means abort it — the in-flight
    // generator must stop producing events into what is about to be a fresh
    // conversation. Do NOT instead disable the button while streaming; the
    // user's intent is to abandon the old turn, not to be blocked from
    // starting a new one.
    abandonTurn();
    setTurn(emptyTurn());
    setShowHistory(false);
    forgetConversation();
  }, [abandonTurn, forgetConversation]);

  // Keyed on turn.blocks only, but effectiveStatus() reads the wall clock
  // (`new Date()`), so this does not recompute purely because time passes —
  // the amber "pending action" badge can outlive an action's expires_at
  // until some other block arrives and this memo re-runs. Actions expire
  // after 24 hours, which makes the window rare; not worth a timer for it.
  const hasPendingActions = useMemo(
    () =>
      turn.blocks.some(
        (block) => block.type === 'confirmation' && block.actions.some((one) => effectiveStatus(one) === 'pending'),
      ),
    [turn.blocks],
  );

  const value = useMemo<AssistantContextValue>(
    () => ({
      status,
      visible: assistantIsVisible(status),
      overlay,
      open: () => setOverlay('open'),
      minimize: () => setOverlay('minimized'),
      close: () => setOverlay('closed'),
      toggle: () => setOverlay((current) => (current === 'open' ? 'minimized' : 'open')),
      showHistory,
      setShowHistory,
      conversationId,
      turn,
      streaming,
      notice,
      error,
      hasPendingActions,
      send,
      resolve,
      stop,
      newConversation,
      openConversation,
    }),
    [
      conversationId, error, hasPendingActions, newConversation, notice, openConversation,
      overlay, resolve, send, showHistory, status, stop, streaming, turn,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
